/*
  <tolstoy-vto> wraps the hidden Tolstoy try-on widget. The "Try it on" badges in
  the product gallery forward their clicks to the widget's own button.

  The widget bundle (~210 KB, React included) loads once the page has finished
  loading and the browser is idle, or sooner on the first sign of intent (hover,
  focus or touch on a badge), so it stays off the critical path.
*/
const BADGE_SELECTOR = '[data-data-tolstoy-vto]';
const CLICK_TIMEOUT = 8000;
const INTENT_EVENTS = ['focusin', 'pointerover', 'touchstart'];

class TolstoyVto extends HTMLElement {
  connectedCallback() {
    this.observers = new Set();
    this.onClick = this.onClick.bind(this);
    this.onIntent = this.onIntent.bind(this);
    this.onLoad = this.onLoad.bind(this);

    document.addEventListener('click', this.onClick);
    INTENT_EVENTS.forEach((type) => {
      document.addEventListener(type, this.onIntent, { passive: true });
    });

    /* ADA: make the widget's hidden button reachable by keyboard */
    this.waitForButton().then((button) => {
      if (button) button.tabIndex = 0;
    });

    if (document.readyState === 'complete') this.onLoad();
    else window.addEventListener('load', this.onLoad, { once: true });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onClick);
    window.removeEventListener('load', this.onLoad);
    this.removeIntentListeners();
    this.observers.forEach((observer) => observer.disconnect());
    this.observers.clear();
  }

  loadWidget() {
    if (this.widgetRequested || !this.dataset.src) return;

    this.widgetRequested = true;
    this.removeIntentListeners();

    const script = document.createElement('script');
    script.async = true;
    script.src = this.dataset.src;
    document.head.appendChild(script);
  }

  onClick(event) {
    if (!event.target.closest(BADGE_SELECTOR)) return;

    this.loadWidget();
    this.waitForButton(CLICK_TIMEOUT).then((button) => {
      if (button) button.click();
    });
  }

  onIntent(event) {
    if (event.target.closest?.(BADGE_SELECTOR)) this.loadWidget();
  }

  onLoad() {
    const idle = window.requestIdleCallback || ((callback) => setTimeout(callback, 200));

    idle(() => {
      /* Skip the download when no badge is visible (e.g. hidden by the AB test) */
      const badgeVisible = [...document.querySelectorAll(BADGE_SELECTOR)].some((badge) => badge.checkVisibility());

      if (!badgeVisible) return;

      this.loadWidget();
    }, { timeout: 3000 });
  }

  removeIntentListeners() {
    INTENT_EVENTS.forEach((type) => {
      document.removeEventListener(type, this.onIntent);
    });
  }

  waitForButton(timeout) {
    return new Promise((resolve) => {
      const findButton = () => this.querySelector('tolstoy-widget button');
      const button = findButton();

      if (button) {
        resolve(button);
        return;
      }

      let timer;

      const observer = new MutationObserver(() => {
        const found = findButton();

        if (!found) return;

        clearTimeout(timer);
        observer.disconnect();
        this.observers.delete(observer);
        resolve(found);
      });

      observer.observe(this, { childList: true, subtree: true });
      this.observers.add(observer);

      if (timeout) {
        timer = setTimeout(() => {
          observer.disconnect();
          this.observers.delete(observer);
          resolve(null);
        }, timeout);
      }
    });
  }
}

if (!customElements.get('tolstoy-vto')) customElements.define('tolstoy-vto', TolstoyVto);
